// i18next configuration for AFCripto
// Backend as Single Source of Truth: translations arrive as one manifest plus one immutable bundle per
// audience and language (see ./bundle-backend.ts), not one request per namespace.

import i18n from 'i18next'
import type { TFunction } from 'i18next'
import { initReactI18next } from 'react-i18next'

// Import API base URL for correct backend targeting
import { API_BASE_URL, STORAGE_KEYS } from '@/shared/utils/constants'
import { logger } from '@/shared/utils/logger'

import { createRequestTimeoutSignal, TranslationBundleBackend } from './bundle-backend'
import { installI18nFailureResilience } from './failure-resilience'
import { applyCriticalFallbackForMissingNamespaces } from './fallback/critical-fallback-resources'
import { resolveMissingTranslationKey } from './missing-key-humanizer'
import { appTranslationLoadStatus } from './translation-load-status'

// Type declarations are in ./i18next.d.ts

/** Code is just a string at runtime — codes come from the DB catalog. */
export type Language = string

/** One language entry as returned by the public backend endpoint. */
export interface AvailableLanguage {
  code: string
  label: string
  isDefault: boolean
}

/**
 * Cache for the dynamic languages list. Resolved once after the first
 * successful fetch; consumers can `await getAvailableLanguages()` to read it.
 */
let availableLanguagesCache: AvailableLanguage[] | null = null
let availableLanguagesPromise: Promise<AvailableLanguage[]> | null = null

/** Static fallback list used when the backend is unreachable. */
const FALLBACK_LANGUAGES: AvailableLanguage[] = [
  { code: 'es', label: 'Español', isDefault: true },
  { code: 'en', label: 'English', isDefault: false },
]

/**
 * Per-request network budgets. `fetch` never times out on its own: a backend
 * that accepts the connection and then stalls used to hang the whole boot
 * (blank `#root`), because the languages call is awaited BEFORE `i18n.init`.
 */
const LANGUAGES_REQUEST_TIMEOUT_MS = 2000

/**
 * Fetch the list of active languages from the backend public endpoint.
 * Memoised — subsequent calls return the cached value until
 * `clearAvailableLanguagesCache()` is invoked.
 *
 * On error, falls back to the hardcoded { es, en } so the UI is always usable.
 */
const fetchAvailableLanguages = async (): Promise<AvailableLanguage[]> => {
  if (availableLanguagesCache) return availableLanguagesCache
  if (availableLanguagesPromise) return availableLanguagesPromise

  availableLanguagesPromise = (async (): Promise<AvailableLanguage[]> => {
    try {
      const url = `${API_BASE_URL}/api/i18n/languages`
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        // Bounded: a hung response must not delay `i18n.init` (and the first
        // paint) — the static FALLBACK_LANGUAGES below keep the UI usable.
        signal: createRequestTimeoutSignal(LANGUAGES_REQUEST_TIMEOUT_MS),
      })
      if (!response.ok) {
        throw new Error(`/api/i18n/languages returned ${response.status}`)
      }
      const body = (await response.json()) as {
        success?: boolean
        data?: AvailableLanguage[]
      }
      if (!body?.success || !Array.isArray(body.data) || body.data.length === 0) {
        throw new Error('Empty or invalid payload from /api/i18n/languages')
      }
      availableLanguagesCache = body.data
      return availableLanguagesCache
    } catch (error) {
      logger.warn('[i18n] Falling back to hardcoded LANGUAGES (fetch failed):', error)
      availableLanguagesCache = FALLBACK_LANGUAGES
      return availableLanguagesCache
    } finally {
      availableLanguagesPromise = null
    }
  })()
  return availableLanguagesPromise
}

/**
 * Synchronous accessor for the languages list. Returns the cached value if
 * present, otherwise the static fallback. Use this in render paths where you
 * cannot `await` — call `fetchAvailableLanguages()` once at boot to warm the
 * cache.
 */
export const getAvailableLanguages = (): AvailableLanguage[] => {
  return availableLanguagesCache ?? FALLBACK_LANGUAGES
}

/** Reset the cache so the next `fetchAvailableLanguages()` re-hits the API. */
export const clearAvailableLanguagesCache = (): void => {
  availableLanguagesCache = null
  availableLanguagesPromise = null
}

// Available namespaces
export const NAMESPACES = [
  'common',
  'auth',
  'transactions',
  'validation',
  'balance',
  'matching',
  'imports',
  'shared',
  'admin',
  'tax-reports',
  'balances',
  'profile',

  'errors',
  'settings',
  'onboarding',
  'notifications',
  'support',
  'portfolio',
] as const

export type Namespace = (typeof NAMESPACES)[number]

/** Return the set of supported language codes — driven by the cached list. */
const getSupportedLanguageCodes = (): string[] => {
  return getAvailableLanguages().map((entry) => entry.code)
}

/**
 * Is `code` one of the languages the platform OFFERS right now (the active
 * catalogue rows)? A saved preference that is no longer offered must not be
 * applied: the super-admin deactivated it, and the dialog promised its readers
 * move to the default language.
 */
export const isOfferedLanguage = (code: string): boolean =>
  getSupportedLanguageCodes().includes(code)

/** Default language code (the row with isDefault=true), or 'es' as fallback. */
const getDefaultLanguageCode = (): string => {
  const list = getAvailableLanguages()
  const defaultEntry = list.find((entry) => entry.isDefault)
  return defaultEntry?.code ?? 'es'
}

// Get browser language preference (matched against the dynamic catalog).
const getBrowserLanguage = (): Language | null => {
  if (typeof window === 'undefined' || !navigator) return null

  try {
    const supported = new Set(getSupportedLanguageCodes())
    const languages = navigator.languages || [navigator.language]

    for (const lang of languages) {
      const languageCode = lang.toLowerCase().split('-')[0]
      if (languageCode && supported.has(languageCode)) {
        return languageCode
      }
    }

    return null
  } catch (error) {
    logger.warn('[i18n] Failed to detect browser language:', error)
    return null
  }
}

// Get saved language with proper priority: URL params → localStorage → browser → default
const getSavedLanguage = (): Language => {
  const fallback = getDefaultLanguageCode()
  if (typeof window === 'undefined') return fallback

  try {
    const supported = new Set(getSupportedLanguageCodes())

    // 1. URL parameters (for E2E testing and direct navigation)
    const urlParams = new URLSearchParams(window.location.search)
    const urlLang = urlParams.get('lang')
    if (urlLang && supported.has(urlLang)) {
      localStorage.setItem(STORAGE_KEYS.LANGUAGE, urlLang)
      return urlLang
    }

    // 2. localStorage
    const saved = localStorage.getItem(STORAGE_KEYS.LANGUAGE)
    if (saved && supported.has(saved)) {
      return saved
    }

    // 3. Browser language preference
    const browserLang = getBrowserLanguage()
    if (browserLang) {
      localStorage.setItem(STORAGE_KEYS.LANGUAGE, browserLang)
      return browserLang
    }

    // 4. Fallback to the platform default (DB-driven)
    return fallback
  } catch (error) {
    logger.warn('[i18n] Failed to determine language, falling back to default:', error)
    return fallback
  }
}

/**
 * How i18next reads and retries the namespaces. Read by its BackendConnector (top-level options, NOT
 * `backend.*`).
 *
 * `maxRetries`/`retryTimeout`: the defaults are 5 retries starting at 350ms with doubling backoff ≈ 11s of
 * stalled boot per failing namespace. Two retries (500ms + 1s) fail fast. i18next only retries a read whose
 * callback says so: TranslationBundleBackend does for transient failures (network, timeout, 5xx, 429, and a
 * bundle 404 that persists after its one manifest refresh), never for another 4xx or a malformed answer.
 * Longer recovery (3 s, 10 s, 30 s later) is handled by installI18nFailureResilience().
 *
 * `maxParallelReads`: i18next reads at most 10 namespaces at once by default and there are 18. All the ones
 * of an audience share ONE request (`TranslationBundleBackend`), so there is nothing to limit; with the
 * limit, the 7 that wait in the queue open a second manifest request on the first failed attempt and may
 * do it on each retry (up to 6 requests where 3 are enough).
 */
export const I18N_LOAD_OPTIONS = {
  maxRetries: 2,
  retryTimeout: 500,
  maxParallelReads: NAMESPACES.length,
} as const

/**
 * The fallback namespace and the interpolation options of production. Shared with the test instances
 * (`test/setup/vitest.setup.ts`, `test/i18n/realAdminI18n.ts`) so a test resolves a key the way production does.
 */
export const I18N_FALLBACK_NAMESPACE = 'common' as const
export const I18N_INTERPOLATION_OPTIONS = {
  escapeValue: false, // React already escapes values
  skipOnVariables: false,
} as const

// i18next configuration - translation bundles served by the backend
export const i18nConfig = {
  // Language settings - load with priority: URL → localStorage → browser → default
  lng: getSavedLanguage(), // Load language with proper priority order
  // No fallback language: the bundle already carries the English strings merged key by key (server side)
  fallbackLng: false as const,

  // Namespace settings
  ns: NAMESPACES,
  defaultNS: 'common' as const,
  fallbackNS: I18N_FALLBACK_NAMESPACE,

  // Development settings
  debug: import.meta.env.DEV,

  // Performance settings
  load: 'languageOnly' as const, // Don't load region-specific variants

  // Key separator (use . for nested keys)
  keySeparator: '.',
  nsSeparator: ':',

  // Retry and parallelism of the namespace reads (see I18N_LOAD_OPTIONS).
  ...I18N_LOAD_OPTIONS,

  // Interpolation settings
  interpolation: { ...I18N_INTERPOLATION_OPTIONS },

  // React settings
  react: {
    useSuspense: false, // Disable suspense for debugging
    bindI18n: 'languageChanged loaded',
    bindI18nStore: 'added removed',
    transEmptyNodeValue: '', // Don't render fallback for empty trans
    transSupportBasicHtmlNodes: true,
    transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'em'], // Allow basic HTML
  },

  // Last-resort readability: a key that resolves to nothing (namespace failed
  // to load, key not in the catalog) is rendered as words instead of leaking
  // the technical path — users saw `fields.email_address` on the login page.
  // An explicit defaultValue at the call site still wins.
  parseMissingKeyHandler: resolveMissingTranslationKey,

  // Error handling
  saveMissing: import.meta.env.DEV,
  missingKeyHandler: (lngs: readonly string[], ns: string, key: string, _fallbackValue: string) => {
    if (import.meta.env.DEV) {
      logger.warn(`[i18n] Missing translation key: ${ns}:${key} for language: ${lngs.join(', ')}`)
    }
  },

  // Performance optimizations
  cleanCode: true, // Clean up resources
  returnObjects: false, // Don't return objects as strings
}

/** Guard so the failed-load listener is registered exactly once per page. */
let isFailureResilienceInstalled = false

/**
 * Initialize i18next with the translation bundle backend.
 *
 * Issue #330: first hits `/api/i18n/languages` so the resulting i18n instance
 * knows the *real* set of supported language codes (driven by the DB catalog,
 * not by a hardcoded `['es', 'en']` tuple).
 */
export const initializeI18n = async (): Promise<typeof i18n> => {
  // Hydrate the languages cache BEFORE i18next.init so `supportedLngs` /
  // `lng` reflect the DB catalog. On failure, `fetchAvailableLanguages`
  // returns the static fallback list so the UI still boots.
  await fetchAvailableLanguages()

  // `en` stays loadable even when a super-admin deactivated it in the switcher: a user may still pick
  // it, and the bundle of every language is English-complete anyway. Which languages are OFFERED is
  // `getSupportedLanguageCodes()`; which can be LOADED is this list.
  const supportedLngs = [...new Set([...getSupportedLanguageCodes(), 'en'])]

  // Recompute `lng` now that the cache is hot — getSavedLanguage() consults
  // `getSupportedLanguageCodes()` which reads the same cache.
  const dynamicConfig = {
    ...i18nConfig,
    lng: getSavedLanguage(),
    supportedLngs,
  }

  // Self-heal failed namespace loads (backend restart / transient network):
  // seed the bundled critical fallback immediately + bounded background
  // retries. Installed BEFORE init so the very first failure is caught, and
  // only ONCE so a repeated initializeI18n() cannot stack listeners.
  if (!isFailureResilienceInstalled) {
    installI18nFailureResilience(i18n, appTranslationLoadStatus)
    isFailureResilienceInstalled = true
  }

  await i18n.use(TranslationBundleBackend).use(initReactI18next).init(dynamicConfig)

  // Set initial document language for accessibility and E2E testing
  if (typeof window !== 'undefined' && document) {
    document.documentElement.lang = i18n.language
  }

  // Development utilities
  if (import.meta.env.DEV) {
    // Make i18n instance available globally for debugging
    ;(window as unknown as Record<string, unknown>)['__i18n'] = i18n
  }

  return i18n
}

// Export configured instance
export default i18n

/**
 * Seed the bundled critical fallback on the app instance for any namespace the
 * backend has not delivered. Called by the boot sequence when the budget
 * expires — a no-op once every namespace loaded, and it never shadows a
 * namespace that DID load (see applyCriticalFallbackForMissingNamespaces).
 */
export const applyCriticalTranslationFallback = (): string[] =>
  applyCriticalFallbackForMissingNamespaces(i18n)

// Export utilities
export const changeLanguage = (lng: Language): Promise<TFunction> => i18n.changeLanguage(lng)
export const isLanguageLoaded = (lng: Language): boolean => i18n.hasResourceBundle(lng, 'common')
